import { axiosDelete, axiosGet, axiosPost, axiosPut } from "./ajaxServices";
import { urlRegisterUser } from "./CONSTANTS";
import api from "./api.js";

/**
 * Calculate age in whole years from a birthday string.
 * Handles formats: MM/DD/YY, MM/DD/YYYY, YYYY-MM-DD.
 * Returns a string like "12", or null if the birthday cannot be parsed.
 */
const calculateAgeFromBirthday = (birthday) => {
  if (!birthday) return null;
  let date;
  if (/^\d{4}-\d{2}-\d{2}$/.test(birthday)) {
    date = new Date(birthday + 'T00:00:00');
  } else if (/^\d{1,2}\/\d{1,2}\/\d{2}$/.test(birthday)) {
    const [m, d, y] = birthday.split('/');
    const fullYear = parseInt(y) <= 30 ? 2000 + parseInt(y) : 1900 + parseInt(y);
    date = new Date(fullYear, parseInt(m) - 1, parseInt(d));
  } else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(birthday)) {
    const [m, d, y] = birthday.split('/');
    date = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
  } else {
    date = new Date(birthday);
  }
  if (!date || isNaN(date.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const monthDiff = today.getMonth() - date.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
    age--;
  }
  return age > 0 ? String(age) : null;
};

/**
 * Enrich a camperInfo array: for any camper that has camper_birthday but
 * lacks camper_age (empty/null/undefined), compute and fill it in.
 */
const enrichCamperAges = (camperInfoArray) => {
  if (!Array.isArray(camperInfoArray)) return camperInfoArray;
  return camperInfoArray.map((camper) => {
    if (camper.camper_birthday && !camper.camper_age) {
      const computedAge = calculateAgeFromBirthday(camper.camper_birthday);
      if (computedAge) return { ...camper, camper_age: computedAge };
    }
    return camper;
  });
};

/**
 * Report a database error to support after a successful payment
 * This is called silently - user should not see any error since payment was successful
 */
export const apiReportDbError = async (errorDetails) => {
  try {
    const params = new FormData();
    params.append("error_type", "db_error_after_payment");
    params.append("error_message", errorDetails.errorMessage || "Unknown error");
    params.append("transaction_id", errorDetails.transaction_id || "");
    params.append("payment_gateway", errorDetails.payment_gateway || "");
    params.append("total_checkout", errorDetails.total_checkout || "");
    params.append("registration_id", errorDetails.registration_id || "");
    params.append("registration_name", errorDetails.registration_name || "");
    params.append("customer_email", errorDetails.p_email || "");
    params.append("customer_name", `${errorDetails.p_first_name || ""} ${errorDetails.p_last_name || ""}`);
    params.append("customer_phone", errorDetails.p_cell_phone || "");
    params.append("full_payload", JSON.stringify(errorDetails.fullPayload || {}));
    params.append("timestamp", new Date().toISOString());

    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    };

    await api.post("camp/report_registration_error", params, config);
  } catch (e) {
    // Silently fail - we don't want to show any error to the user
    console.error("[apiReportDbError] Failed to report error:", e);
  }
};

const hasTrips = (selTrip) => {
  if (!selTrip || typeof selTrip !== 'string') return false;
  return selTrip.replace(/,/g, '').trim().length > 0;
};

const hasCamperData = (camper) => {
  return Object.keys(camper).some((key) => {
    if (['sel_reg_id', 'sel_trip', 'src_fee', 'real_fee', 'sibling_discount',
         'zip_discount', 'zip_include', 'sibling_include'].includes(key)) return false;
    const val = camper[key];
    return val !== undefined && val !== null && val !== '' && val !== 0;
  });
};

const buildRegistrationFormData = (param) => {
  const params = new FormData();
  params.append("registration_id", param.registration_id);
  params.append("registration_name", param.registration_name);
  params.append("yaccamp", param.yaccamp);
  params.append("reg_type", param.reg_type);

  const filteredCamperInfo = param.camperInfo.filter(
    (camper, index) => index === 0 || hasCamperData(camper) ||
      (camper.sel_reg_id && camper.sel_reg_id.length > 0) || hasTrips(camper.sel_trip)
  );
  const enrichedCamperInfo = enrichCamperAges(filteredCamperInfo);

  params.append("camper_info", JSON.stringify(enrichedCamperInfo));
  params.append("camper_reg", enrichedCamperInfo.filter((f) => f.sel_reg_id.length !== 0).length > 0);
  params.append("customer_data", JSON.stringify(param.userData));
  params.append("waiver_info", JSON.stringify(param.waiverList));

  params.append("bill_first_name", param.bill_first_name || "");
  params.append("bill_last_name", param.bill_last_name || "");
  params.append("bill_address", param.bill_address || "");
  params.append("bill_city", param.bill_city || "");
  params.append("bill_postal_code", param.bill_postal_code || "");
  params.append("bill_state", param.bill_state || "");

  params.append("discount_code", param.discount_code || "");
  params.append("discount_amount", param.discount_amount || 0);
  params.append("processing_fee", param.processing_fee || 0);
  params.append("total_checkout", param.total_checkout || 0);
  params.append("total_amount", param.src_total_amount || 0);
  params.append("early_reg_discount", param.early_reg_discount_amount_applied || 0);
  params.append("late_reg_fee", param.late_reg_fee_amount_applied || 0);
  params.append("linked_transaction_id", param.linked_transaction_id || "");

  for (let i = 0; i < (param.recordList || []).length; i++) {
    params.append("camper_records[]", param.recordList[i]);
  }

  return { params, enrichedCamperInfo };
};

export const apiRegisterUser = async (param) => {
  const { params } = buildRegistrationFormData(param);

  params.append("payment_gateway", param.isStax ? "stax" : "forte");
  params.append("payment_method", param.payment_method);
  params.append("transaction_id", param.transaction_id);
  params.append("entered_by", param.entered_by);
  params.append("authorization_code", param.authorization_code);
  params.append("location_id", param.location_id);

  const config = { headers: { "Content-Type": "multipart/form-data" } };
  const { data } = await api.post("camp/register_user_dynamic", params, config);
  return data;
};

export const apiRegisterAndPay = async (param) => {
  const { params } = buildRegistrationFormData(param);

  params.append("payment_gateway", "forte");
  params.append("payment_method", param.payment_method);
  params.append("one_time_token", param.one_time_token);
  params.append("authorization_amount", param.authorization_amount);
  params.append("service_fee_amount", param.service_fee_amount || 0);
  params.append("billing_address", JSON.stringify(param.billing_address || {}));
  params.append("card_type", param.card_type || "credit");
  params.append("domain", param.domain);

  const config = { headers: { "Content-Type": "multipart/form-data" } };
  const { data } = await api.post("camp/register_and_pay", params, config);
  return data;
};

export const apiRegisterFailedPayment = async (param) => {
  const params = new FormData();
  params.append("err_res", param.err_res);
  params.append("api_name", param.api_name);
  params.append("p_cell_phone", param.p_cell_phone);
  params.append("p_email", param.p_email);
  params.append("p_first_name", param.p_first_name);
  params.append("p_home_phone", param.p_home_phone == undefined ? "" : param.p_home_phone);
  params.append("p_last_name", param.p_last_name);
  params.append("discount_code", param.discount_code == undefined ? "" : param.discount_code);
  params.append("discount_amount", param.discount_amount);
  params.append("processing_fee", param.processing_fee);
  params.append("total_checkout", param.total_checkout);
  params.append("total_amount", param.src_total_amount);
  params.append("early_reg_discount", param.early_reg_discount_amount_applied || 0);
  params.append("late_reg_fee", param.late_reg_fee_amount_applied || 0);
  params.append("payment_method", param.payment_method || "");

  // Billing info
  params.append("bill_first_name", param.bill_first_name || "");
  params.append("bill_last_name", param.bill_last_name || "");
  params.append("bill_address", param.bill_address || "");
  params.append("bill_city", param.bill_city || "");
  params.append("bill_state", param.bill_state || "");
  params.append("bill_postal_code", param.bill_postal_code || "");

  // Card details (last 4 only, no CVV for PCI compliance)
  params.append("card_last4", param.card_last4 || "");
  params.append("expiry", param.expiry || "");

  // Full merchant error response
  params.append("merchant_error_detail", param.merchant_error_detail || "");

  const config = {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  };

  const { data } = await api.post("camp/register_failed_user", params, config);

  return data;
};

export const apiInitiateLinkedReg = async (email, registration_id) => {
  const params = new FormData();
  params.append("email", email);
  params.append("registration_id", registration_id);
  const { data } = await api.post("camp/initiate_linked_reg", params, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
};

export const apiSendLinkedOtp = async (email, registration_id, delivery_method) => {
  const params = new FormData();
  params.append("email", email);
  params.append("registration_id", registration_id);
  params.append("delivery_method", delivery_method);
  const { data } = await api.post("camp/send_linked_otp", params, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
};

export const apiVerifyLinkedOtp = async (email, code, registration_id) => {
  const params = new FormData();
  params.append("email", email);
  params.append("code", code);
  params.append("registration_id", registration_id);
  const { data } = await api.post("camp/verify_linked_otp", params, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
};

export const apiLogRegistrationStep = async (step, _data) => {
  const params = new FormData();
  params.append("step", step);
  params.append("registration_id", _data.registration_id);
  params.append("registration_name", _data.registration_name);
  params.append("reg_type", _data.reg_type);
  

  const appendFieldSafely = (key, value) => {
    // Ensure value is not undefined or null, otherwise send empty string
    params.append(key, (value !== undefined && value !== null) ? value : "");
  };

  // Step 1 data
  if (step >= 1) {
    // All campers info - send as JSON array
    if (_data.camperInfo && _data.camperInfo.length > 0) {
      // Compute camper_age from camper_birthday for any camper that is missing it
      const enrichedCamperInfo = enrichCamperAges(_data.camperInfo);
      params.append("camper_info", JSON.stringify(enrichedCamperInfo));
      params.append("camper_count", enrichedCamperInfo.length);

      // Also send first camper fields for backwards compatibility
      const firstCamper = enrichedCamperInfo[0];
      appendFieldSafely("camper_first_name", firstCamper.camper_first_name);
      appendFieldSafely("camper_last_name", firstCamper.camper_last_name);
      appendFieldSafely("camper_age", firstCamper.camper_age);
      appendFieldSafely("camper_birthday", firstCamper.camper_birthday);
      appendFieldSafely("camper_email", firstCamper.camper_email);
      appendFieldSafely("camper_gender", firstCamper.camper_gender);
      appendFieldSafely("camper_grade", firstCamper.camper_grade);
      appendFieldSafely("camper_street", firstCamper.camper_street);
      appendFieldSafely("camper_city", firstCamper.camper_city);
      appendFieldSafely("camper_state", firstCamper.camper_state);
      appendFieldSafely("camper_zip", firstCamper.camper_zip);
      appendFieldSafely("camper_t_shirt", firstCamper.camper_t_shirt);
      appendFieldSafely("camper_health", firstCamper.camper_health);
    }
    // Parent info
    appendFieldSafely("p_first_name", _data.p_first_name);
    appendFieldSafely("p_last_name", _data.p_last_name);
    appendFieldSafely("p_email", _data.p_email);
    appendFieldSafely("p_cell_phone", _data.p_cell_phone);
    appendFieldSafely("p_home_phone", _data.p_home_phone);
    appendFieldSafely("p_relation", _data.p_relation);
  }

  // Step 2 data
  if (step >= 2) {
    // Registration and trip selections for all campers
    if (_data.camperInfo && _data.camperInfo.length > 0) {
      // Send all campers' selections as JSON
      const allSelections = _data.camperInfo.map((camper, idx) => ({
        camper_index: idx,
        camper_name: `${camper.camper_first_name || ''} ${camper.camper_last_name || ''}`.trim(),
        sel_trip: camper.sel_trip || '',
        sel_reg_id: Array.isArray(camper.sel_reg_id) ? camper.sel_reg_id : []
      }));
      params.append("all_camper_selections", JSON.stringify(allSelections));

      // Backwards compatibility - first camper
      const firstCamper = _data.camperInfo[0];
      params.append("camper_sel_trip", firstCamper.sel_trip || '');
      params.append("camper_sel_reg_id", Array.isArray(firstCamper.sel_reg_id) ? firstCamper.sel_reg_id.map(r => r.id || r).join(',') : '');
    }
    params.append("camper_reg", _data.camper_reg || '');
    
    // Waivers
    params.append("waivers", JSON.stringify(_data.waiverList));
  }
  
  // Step 3 data
  if (step >= 3) {
    // Billing info
    params.append("bill_first_name", _data.bill_first_name);
    params.append("bill_last_name", _data.bill_last_name);
    params.append("bill_address", _data.bill_address);
    params.append("bill_city", _data.bill_city);
    params.append("bill_postal_code", _data.bill_postal_code);
    params.append("bill_state", _data.bill_state);
    
    // Payment info
    params.append("discount_code", _data.discount_code);
    params.append("discount_amount", _data.discount_amount);
    params.append("processing_fee", _data.processing_fee);
    params.append("total_amount", _data.total_amount);
    params.append("total_checkout", _data.total_checkout);
    params.append("payment_method", _data.payment_method);
    params.append("payment_gateway", _data.isStax ? "stax" : "forte");
    
    // Additional payment fields
    params.append("sum_discount_amount", _data.discount_amount);
    params.append("sum_processing_fee", _data.processing_fee);
    params.append("sum_total_amount", _data.total_amount);
    params.append("sum_total_checkout", _data.total_checkout);
  }
  
  const config = {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  };

  try {
    const { data } = await api.post("camp/log_registration_step", params, config);
    return data;
  } catch (error) {
    console.error("[apiLogRegistrationStep] Error:", {
      error,
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status
    });
    // Re-throw with a proper error object
    throw new Error(error?.response?.data?.message || error?.message || "Failed to log registration step");
  }
};
